// KINGDOM VIEW modal — opens when the player clicks a kingdom tab. Shows that
// kingdom's stats (wealth, citizens, buildings count) read-only, plus a DEFECT
// button when the kingdom is NOT the player's home.
//
// In Phase 5.1 the stats come from local state (kingdoms[id] slice). When the
// backend lands in Phase 5.2 these can read from server-aggregated data.

import { Events, on, emit } from './eventBus.js'
import { KingdomConfig, GoonTypes, DefectionConfig } from '../config/balance.js'
import { getKingdom, getProfile } from '../game/state.js'
import { canDefect } from '../game/profileManager.js'
import { fetchKingdomAggregates } from '../game/accountManager.js'
import { formatCoins } from './format.js'

const FORMATTER = new Intl.NumberFormat('en-US')

// Minimal HTML escape so player-supplied cat names can't break out of the
// top-players row template.
function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c])
}

// Flatten every roster seat in this kingdom (Noble / Military / Clergy +
// any future elected titles), sort by wealth descending, dedupe by id, and
// return the top 8 as { id, name, title, wealth, isNpc }. Drives the rank
// list when the backend isn't supplying aggregates.
function localTopFromRosters(k, kingdomId) {
  const rosters = k?.rosters ?? {}
  const seen = new Set()
  const flat = []
  for (const [titleId, roster] of Object.entries(rosters)) {
    for (const entry of roster ?? []) {
      if (!entry?.id || seen.has(entry.id)) continue
      seen.add(entry.id)
      flat.push({
        id: entry.id,
        name: entry.name,
        title: titleId,
        wealth: entry.wealth ?? 0,
        kingdom: kingdomId,
        openToMarriage: entry.openToMarriage ?? true,
        isNpc: !entry.isPlayer,
      })
    }
  }
  flat.sort((a, b) => b.wealth - a.wealth)
  return flat.slice(0, 8)
}

export function mountKingdomViewPanel({ root }) {
  root.innerHTML = `
    <div class="hud__kview-card">
      <div class="hud__kview-header">
        <h2 data-kview-title>—</h2>
        <button class="hud__kview-close" data-kview-close>×</button>
      </div>
      <div class="hud__kview-body" data-kview-body></div>
      <div class="hud__kview-footer" data-kview-footer></div>
    </div>
  `

  const els = {
    title: root.querySelector('[data-kview-title]'),
    body: root.querySelector('[data-kview-body]'),
    footer: root.querySelector('[data-kview-footer]'),
    close: root.querySelector('[data-kview-close]'),
  }

  const close = () => root.classList.remove('active')
  els.close.addEventListener('click', close)
  root.addEventListener('click', (e) => {
    if (e.target === root) close() // backdrop click
    // Sort toggle. Re-render in place; aggregateCache + viewingId unchanged.
    const sortBtn = e.target.closest('[data-kview-sort]')
    if (sortBtn) {
      const next = sortBtn.dataset.kviewSort
      if (next && next !== sortMode && SORTS[next]) {
        sortMode = next
        render()
      }
      return
    }
    // Rank-list row → inspect that player's Registry. Emit a structured
    // event the Registry panel listens for; close this modal so the
    // Registry can take focus.
    const row = e.target.closest('[data-kview-player]')
    if (row) {
      try {
        const target = JSON.parse(row.dataset.kviewPlayer)
        emit(Events.RegistryOpen, { target })
        close()
      } catch { /* a row without valid data: ignore the click */ }
    }
  })

  let viewingId = null
  let aggregateCache = null
  // Sort mode for the rank list. Persists across opens (panel-instance scope).
  let sortMode = 'wealth' // 'wealth' | 'citizens' | 'achievements'

  // Comparator + right-side value-formatter per sort mode. Wealth uses the
  // existing money format; citizens/achievements use plain integers.
  const SORTS = {
    wealth:       { cmp: (a, b) => (b.wealth ?? 0) - (a.wealth ?? 0),
                    rhs: (p) => formatCoins(p.wealth ?? 0) },
    citizens:     { cmp: (a, b) => (b.citizens ?? 0) - (a.citizens ?? 0),
                    rhs: (p) => `${FORMATTER.format(p.citizens ?? 0)} citizens` },
    achievements: { cmp: (a, b) => (b.achievements ?? 0) - (a.achievements ?? 0),
                    rhs: (p) => `🏆 ${p.achievements ?? 0}` },
  }

  const render = () => {
    if (!viewingId) return
    const k = getKingdom(viewingId)
    const cfg = KingdomConfig[viewingId]
    const isHome = getProfile().homeKingdom === viewingId
    els.title.innerHTML = `${cfg.name} <span class="sub">${cfg.subtitle}</span>`

    // Default to local kingdom slice; override with server aggregates if we
    // have them (real multiplayer totals from all players in this kingdom).
    const aggregate = aggregateCache?.[viewingId]
    const localCitizens = Object.values(GoonTypes).reduce(
      (sum, id) => sum + (k.goons?.[id] ?? 0),
      0,
    )
    const citizens = aggregate?.citizens ?? localCitizens
    const wealth = aggregate?.wealth ?? k.wealth
    const players = aggregate?.players ?? (isHome ? 1 : 0)
    // Local fallback: until backend aggregates land, build a "top players"
    // list from this kingdom's existing NPC rosters (Noble / Military /
    // Clergy seats). Sorted by wealth descending, deduped, top 8.
    // Server sends 15 candidates; client picks the sort and slices to 8.
    const rawTop = aggregate?.topPlayers ?? localTopFromRosters(k, viewingId)
    const sort = SORTS[sortMode] ?? SORTS.wealth
    const top = [...rawTop].sort(sort.cmp).slice(0, 8)

    els.body.innerHTML = `
      <div class="hud__kview-stats">
        <div><span class="label">WEALTH</span><span class="val">${formatCoins(wealth)}</span></div>
        <div><span class="label">CITIZENS</span><span class="val">${FORMATTER.format(citizens)}</span></div>
        <div><span class="label">PLAYERS</span><span class="val">${FORMATTER.format(players)}</span></div>
        <div><span class="label">BUILDINGS</span><span class="val">${k.buildings?.length ?? 0}</span></div>
      </div>
      ${top.length ? `
        <div class="hud__kview-toplist">
          <div class="hud__kview-toplist-header">
            <div class="hud__kview-toplist-title">TOP PLAYERS</div>
            <div class="hud__kview-sort" role="tablist" aria-label="Sort top players">
              ${['wealth', 'citizens', 'achievements'].map((m) => `
                <button data-kview-sort="${m}" class="${sortMode === m ? 'on' : ''}" role="tab" aria-selected="${sortMode === m}">${m}</button>
              `).join('')}
            </div>
          </div>
          ${top.map((p, i) => {
            // For non-wealth sorts, also show the wealth in a small dim chip so
            // the player keeps context for who's actually rich.
            const showWealthChip = sortMode !== 'wealth'
            return `
            <div class="hud__kview-toplist-row" role="button" tabindex="0"
                 data-kview-player='${escape(JSON.stringify({
                    id: p.userId ?? p.id ?? `roster:${viewingId}:${i}`,
                    // userId set ⇒ real registered account; registryPanel will
                    // fetch the public sheet on open. Missing ⇒ NPC from local
                    // rosters and only the embedded fields render.
                    userId: p.userId ?? null,
                    name: p.name,
                    title: p.title ?? '',
                    wealth: p.wealth,
                    kingdom: viewingId,
                    openToMarriage: p.openToMarriage ?? true,
                    isNpc: !p.userId,
                 }))}'>
              <span class="rank">#${i + 1}</span>
              <span class="who">${escape(p.name)}${p.title ? ` <span class="t">${escape(p.title)}</span>` : ''}</span>
              ${showWealthChip ? `<span class="ach" title="Cash on hand">${formatCoins(p.wealth ?? 0)}</span>` : ''}
              <span class="w">${sort.rhs(p)}</span>
            </div>
          `}).join('')}
        </div>
      ` : ''}
      <div class="hud__kview-note">
        ${isHome ? 'This is your home kingdom. You play here.' : 'You may visit, but you cannot play here unless you defect.'}
      </div>
    `
    if (isHome) {
      els.footer.innerHTML = `<button class="hud__onboard-btn ghost" data-kview-close-2>Close</button>`
      els.footer.querySelector('[data-kview-close-2]').addEventListener('click', close)
    } else {
      const affordable = canDefect()
      els.footer.innerHTML = `
        <button class="hud__onboard-btn ghost" data-kview-close-3>Cancel</button>
        <button class="hud__onboard-btn" data-kview-defect ${affordable ? '' : 'disabled'}>
          DEFECT — ${formatCoins(DefectionConfig.cost)}
        </button>
      `
      els.footer.querySelector('[data-kview-close-3]').addEventListener('click', close)
      els.footer.querySelector('[data-kview-defect]').addEventListener('click', () => {
        if (!confirm(`Defect from your home kingdom to ${cfg.name}? This costs ${formatCoins(DefectionConfig.cost)}. Your wealth and household come with you; the old kingdom resets.`)) {
          return
        }
        emit(Events.DefectRequested, { kingdom: viewingId })
        close()
      })
    }
  }

  on(Events.KingdomViewRequested, async ({ kingdom }) => {
    viewingId = kingdom
    render() // immediate paint with local data
    root.classList.add('active')
    // Then upgrade to server aggregates if the backend is reachable.
    try {
      aggregateCache = await fetchKingdomAggregates()
      if (root.classList.contains('active')) render()
    } catch {
      // Offline — keep showing local data.
    }
  })
  on(Events.WealthChange, () => {
    if (root.classList.contains('active')) render()
  })
  on(Events.ProfileChange, () => {
    if (root.classList.contains('active')) render()
  })
}
